import { api } from "./client";

export interface ModifierOut {
  id: number;
  modifier_id: string;
  created_at: string;
}

export interface AdditionOut {
  id: number;
  addition_item_id: number;
  station_id: number;
  quantity: string;
  price: string;
  percent: string;
  total_sum: string;
  /** Процентът идва от „Промяна на цена", не от отстъпка → показва се като
   *  задраскана стара + нова цена, БЕЗ бадж за отстъпка. */
  custom_price: boolean;
  /** Преводите на добавката — пълнят се от `enrich_bill_with_status_names`. */
  item_names: Record<string, string>;
}

export interface BillElementItemOut {
  id: number;
  item_id: number;
  station_id: number;
  quantity: string;
  price: string;
  percent: string;
  total_sum: string;
  status: string;
  status_name: string;
  /** Сервитьорската ос — отделна от кухненския статус ("0".."3"). */
  served: boolean;
  locked_price: boolean;
  /** Редът е продаден от обедно меню (org menus.id); null = обикновен ред. */
  lunch_menu_id?: number | null;
  /** Процентът идва от „Промяна на цена", не от отстъпка (виж AdditionOut). */
  custom_price: boolean;
  item_names: Record<string, string>;
  modifiers: ModifierOut[];
  additions: AdditionOut[];
}

export interface BillElementOut {
  id: number;
  waiter_id: number;
  order_time: string;
  comment: string | null;
  items: BillElementItemOut[];
}

export interface DiscountAppliedOut {
  client_id: number;
  client_name: string;
  percent: number;
  discount_type: string;
}

// Едно теглене от картов терминал по сметката — ред от регистъра
// `card_payments` на BE (docs/card-payments.md, кросрепо ред 15).
// `amount` идва като НИЗ през response_model-ите (историята/справката) и като
// ЧИСЛО през dict отговорите (/bills) — винаги Number() преди аритметика.
export interface BillCardPayment {
  id: number;
  bill_id: number | null;
  bill_opened_at: string | null;
  printer_guid: string | null;
  terminal_txn_id: number | null;
  // "pc" (терминал на касата) | "fiscal" (терминал на принтера)
  mount: string;
  // consumed = пари + бон · unknown/void_failed = пари БЕЗ бон (тагът
  // „платена с карта — без бон") · voided = взети и веднага върнати
  status: string;
  amount: string | number;
  rrn: string | null;
  auth_id: string | null;
  masked_pan: string | null;
  receipt_number: string | null;
  request_id: string;
  refund_rrn: string | null;
  refund_note: string | null;
  created_at: string;
  updated_at?: string | null;
}

export interface BillOut {
  id: number;
  name: string;
  total: string;
  total_amount: string;
  total_discount: string;
  // Знаков процент на закачения клиент (<0 = надбавка). null = сметка без
  // клиент; 0 = клиент с 0% (валидно). „Има ли клиент" се гледа по
  // discount_applied, не по процента.
  discount_percent: number | null;
  waiter_id: number | null;
  customer_id: number | null;
  created_at: string;
  updated_at: string;
  status: string;
  closed_at: string | null;
  payment_method_id: number;
  // Кога сметката за последно е възстановена (отворена наново със същото id).
  restored_at?: string | null;
  // Теглене „пари взети, бон няма" по тази сметка (само closed + total 0) —
  // тагът „платена с карта — без бон". null/липсва при всичко останало.
  card_payment_pending?: BillCardPayment | null;
  elements: BillElementOut[];
  discount_applied: DiscountAppliedOut | null;
  // Настанената резервация на отворената сметка — името на гостите върху
  // картата на масата. Само в списъка `GET /bills` (BE
  // `service_notice.seated_reservations_of_bills`); липсва в единичните
  // отговори. В локален режим касата връща остаряло копие на облачното поле —
  // `TableList` не го показва там.
  reservation?: BillReservationTag | null;
  // Броят гости (`bills.guests`; null = не е въвеждан) — иконката върху
  // картата на масата. Само в списъка `GET /bills`, като `reservation`.
  guests?: number | null;
}

/** `reservation` по сметка в `GET /bills` — без телефон (сервитьорите нямат
 *  правото на модул „Резервации"). Името е за картата на масата, останалото —
 *  за лентата с резервацията над поръчаното (`BillReservationBanner`). */
export interface BillReservationTag {
  id: number;
  guest_name: string;
  // ISO моменти — планираното начало и край на резервацията.
  start_at: string;
  end_at: string;
  guests: number;
  // По реда на резервацията (първата е масата на главната сметка).
  table_names: string[];
  // Забележката от резервацията (повод, алергии…); празната — null.
  note: string | null;
}

export interface FiscalResult {
  request_id: string;
  status: "success" | "failed" | "timeout";
  error?: string;
  result?: Record<string, unknown>;
}

export interface CloseBillResult extends BillOut {
  fiscal_result?: FiscalResult;
}

export interface ModifierIn {
  modifier_id: string;
}

export interface AdditionIn {
  addition_item_id: number;
  station_id: number;
  quantity: string;
  price: string;
  percent: string;
  total_sum: string;
}

export interface ProductElementIn {
  productId: number;
  stationId: number;
  quantity: string;
  price: string;
  lockedPrice: boolean;
  percent: string;
  lineTotal: string;
  status?: string;
  modifiers?: ModifierIn[];
  addons?: AdditionIn[];
  /** Ред от обедно меню — `price` е обедната цена (виж lib/lunchCatalog.ts). */
  lunchMenuId?: number;
}

// Клиент, избран преди откриването (нова маса) — BE го закача веднага след
// създаването на сметката; същите полета като ApplyDiscountIn без billId.
export interface NewBillDiscountIn {
  clientId: number;
  // знаков процент: >0 = отстъпка, <0 = надбавка
  percent: number;
  clientName: string;
  discountType: string;
}

export interface CreateBillIn {
  billId: number;
  tableName?: string;
  elements: ProductElementIn[];
  comment?: string;
  // Само при billId=0 (нова маса); при добавяне към съществуваща се игнорира
  discount?: NewBillDiscountIn;
  // Само при billId=0: броят гости от модала преди „Нова маса" (настройка
  // „Въвеждане на брой гости" = при нова маса). BE: 1–999.
  guests?: number;
}

export interface CancelBillItemIn {
  billElementId: number;
  billElementItemId: number;
  quantity?: number;
  reason?: string;
  isDefective?: boolean;
}

export interface SplitBillElementIn {
  billElementId: number;
  quantity: number;
}

export interface SplitBillIn {
  splitTableName: string;
  comment?: string | null;
  elements: SplitBillElementIn[];
  splitInExistingBill?: boolean;
  targetWaiterId?: number | null;
}

export interface UpdateItemStatusIn {
  itemId: number;
  billId: number;
  billElementId: number;
  // Уникален ид на конкретния ред (bill_element_items.id, а при добавка —
  // bill_element_item_additions.id). Когато е подаден, бекендът сменя статуса
  // само на този ред — нужно е, защото един продукт може да е на няколко
  // отделни реда в една поръчка (всяко добавяне при маркиране е отделен ред).
  billElementItemId?: number | null;
  isAddition?: boolean;
  status: "0" | "1" | "2" | "3";
  // Продукт на няколко станции (напр. Скара + Студена): станцията на
  // ЧАСТТА, чийто статус се сменя. Без нея (или за ред с една станция)
  // статусът важи за целия ред — така праща шпикерът. Мониторът я слага
  // само на части, никога на ред с една станция.
  stationId?: number;
}

export interface UpdateItemServedIn {
  billId: number;
  billElementId: number;
  /** bill_element_items.id — сервира се конкретният продуктов ред. */
  billElementItemId: number;
  served: boolean;
}

export interface UpdateItemStatusResponse {
  message: string;
  item_id: number;
  new_status: string;
}

export const getBills = async (): Promise<BillOut[]> => {
  const response = await api.get<BillOut[]>("/bills");
  return response.data;
};

// Проверката е ЕДНА заявка и нищо повече. Дотук при провал се правеше ВТОРА
// (`getBills()`) „ако endpoint-ът не съществува" — а той съществува и в
// облака, и на касата (`FiscalAPI app/edge/bills.py`:
// `GET /bills/check-table-name/{name}`). Тоест единственото, което резервът
// още вършеше, беше да УДВОИ чакането точно в аварията: 8 s за проверката
// плюс 8 s за списъка, преди сервитьорът да види каквото и да е. Провали ли
// се проверката, извикващият го казва и човекът опитва пак — повторението
// срещу касата е работа на интерсептора (`api/client.ts` §Повторение).
/**
 * Масата с това име се държи от резервация СЕГА (BE:
 * `reservations/service_notice.py`): настанена, чиято сметка е отворена
 * (напр. втората маса на резервация за 1+2), или предстояща, която започва в
 * следващия час / закъснява. Само за предупреждение — отварянето не спира.
 * Времената са в UTC с „Z".
 */
export interface TableReservationNotice {
  guestName: string;
  guests: number;
  status: "seated" | "upcoming";
  startAt: string;
  seatedAt: string | null;
  // Само при настанена: масата е ДРУГАТА маса на резервацията, сметката ѝ е
  // на тази („1") — отварянето е забранено (BE → 400), Маркиране показва отказ.
  billTableName?: string | null;
}

export interface TableNameCheck {
  exists: boolean;
  // null — няма резервация (или по-стар облак/каса без полето).
  reservation: TableReservationNotice | null;
}

// `billId` — „Смяна на маса" на тази сметка: при лична номерация номерът се
// сверява с масите на СОБСТВЕНИКА ѝ (масата на Мария → масите на Мария), не
// на питащия; без него (нова маса) — с масите на питащия.
export const checkTableName = async (
  tableName: string,
  billId?: number,
): Promise<TableNameCheck> => {
  const response = await api.get<{ exists: boolean; reservation?: TableReservationNotice | null }>(
    `/bills/check-table-name/${encodeURIComponent(tableName)}`,
    billId ? { params: { billId } } : undefined,
  );
  return { exists: response.data.exists, reservation: response.data.reservation ?? null };
};

export interface NextTableName {
  name: string;
  // Резервацията, която държи предложената маса сега (като в
  // `check-table-name`); null — няма (или касата/по-стар облак без полето).
  reservation: TableReservationNotice | null;
}

// Предложен номер за „Нова маса" при бързо закриване. Смята се в BE-то,
// защото сервитьор без view_all_users_bills не вижда чуждите отворени маси,
// а при изключена „обща номерация" номерът трябва да е свободен глобално.
// Отговорът носи и `reservation` — затова питането „масата има резервация"
// не прави втора заявка `check-table-name`.
export const getNextTableName = async (): Promise<NextTableName> => {
  const response = await api.get<{ name: string; reservation?: TableReservationNotice | null }>(
    "/bills/next-table-name",
  );
  return { name: response.data.name, reservation: response.data.reservation ?? null };
};

export const createOrAddToBill = async (
  data: CreateBillIn,
): Promise<BillOut> => {
  const response = await api.post<BillOut>("/bills", data);
  return response.data;
};

/** Брой гости преди „Закрий" (настройка „Въвеждане на брой гости" = при
 * закриване). Само отворена сметка; право close_bill. Броят не се връща в
 * документа на сметката — касата строи същия документ байт за байт. */
export const setBillGuests = async (
  billId: number,
  guests: number,
): Promise<void> => {
  await api.patch(`/bills/${billId}/guests`, { guests });
};

export const closeBill = async (
  billId: number,
  paymentMethodId: number,
  mixedCashAmount?: number,
  mixedCardAmount?: number,
  total?: number,
): Promise<CloseBillResult> => {
  const payload: {
    paymentMethodId: number;
    mixedCashAmount?: number;
    mixedCardAmount?: number;
    total?: number;
  } = {
    paymentMethodId,
  };

  if (
    paymentMethodId === 3 &&
    mixedCashAmount !== undefined &&
    mixedCardAmount !== undefined
  ) {
    payload.mixedCashAmount = mixedCashAmount;
    payload.mixedCardAmount = mixedCardAmount;
  }

  if (total !== undefined) {
    payload.total = total;
  }

  const response = await api.post<CloseBillResult>(
    `/bills/${billId}/close`,
    payload,
  );
  return response.data;
};

export const cancelBillItem = async (
  billId: number,
  data: CancelBillItemIn,
): Promise<BillOut> => {
  const response = await api.post<BillOut>(
    `/bills/${billId}/cancel-item`,
    data,
  );
  return response.data;
};

export interface SplitBillResponse {
  oldBill: BillOut;
  newBill: BillOut;
}

export const splitBill = async (
  billId: number,
  data: SplitBillIn,
): Promise<SplitBillResponse> => {
  const response = await api.post<SplitBillResponse>(
    `/bills/${billId}/split`,
    data,
  );
  return response.data;
};

// --- Split & pay ---
// Маркираме избрани продукти от сметката в нова сметка с автоматично име
// (напр. 101 → 101-001, 101-002 …) и веднага я затваряме с посочения
// метод на плащане. Името се формира от бекенда — фронтендът не го подава.
export interface SplitAndPayBillIn {
  elements: SplitBillElementIn[];
  paymentMethodId: number;
  mixedCashAmount?: number;
  mixedCardAmount?: number;
  // При close_bill_1 → 5 клика на "в брой" изпраща total=0 (cash-only режим)
  total?: number;
}

export interface SplitAndPayBillResponse {
  oldBill: BillOut;
  newBill: BillOut;
  fiscal_result?: FiscalResult;
}

export const splitAndPayBill = async (
  billId: number,
  data: SplitAndPayBillIn,
): Promise<SplitAndPayBillResponse> => {
  const response = await api.post<SplitAndPayBillResponse>(
    `/bills/${billId}/split-and-pay`,
    data,
  );
  return response.data;
};

export const updateItemStatus = async (
  data: UpdateItemStatusIn,
): Promise<UpdateItemStatusResponse> => {
  const response = await api.patch<UpdateItemStatusResponse>(
    "/bills/items/status",
    data,
  );
  return response.data;
};

export const updateItemServed = async (
  data: UpdateItemServedIn,
): Promise<{ message: string; item_id: number; served: boolean }> => {
  const response = await api.patch<{
    message: string;
    item_id: number;
    served: boolean;
  }>("/bills/items/served", data);
  return response.data;
};

export interface ApplyDiscountIn {
  billId: number;
  clientId: number;
  percent: number;
  clientName: string;
  discountType: string;
}

export const applyDiscount = async (
  billId: number,
  data: ApplyDiscountIn,
): Promise<BillOut> => {
  const response = await api.post<BillOut>(
    `/bills/${billId}/apply-discount`,
    data,
  );
  return response.data;
};

export const removeDiscount = async (billId: number): Promise<BillOut> => {
  const response = await api.post<BillOut>(`/bills/${billId}/remove-discount`);
  return response.data;
};

export interface ItemDiscountLineIn {
  billElementItemId: number;
  /** Колко бройки от реда получават процента; по-малко от реда → BE го разделя. */
  quantity: number;
  /** 0 = премахване на отстъпката. */
  percent: number;
}

export interface ApplyItemDiscountsIn {
  billId: number;
  lines: ItemDiscountLineIn[];
}

export interface SetItemPriceLineIn {
  billElementItemId: number;
  /** null = самият продукт; иначе id на добавката (`bill_element_item_additions.id`). */
  additionId: number | null;
  /** Новата единична цена на реда (не сумата). */
  price: number;
}

export interface SetItemPricesIn {
  billId: number;
  lines: SetItemPriceLineIn[];
}

export interface CancelAllItemsIn {
  reason?: string | null;
  isDefective?: boolean;
  // Сторно на сметка, платена с карта: облакът няма път до терминала —
  // операторът връща сумата от него и ПО ИЗБОР вписва референцията.
  // Сторното НЕ се блокира без нея (BE не я изисква).
  refundRrn?: string | null;
  refundNote?: string | null;
}

export interface RestoreBillIn extends CancelAllItemsIn {
  // Виж `transferBillWaiter`: възстановената сметка взима старото име и може да
  // дублира вече отворена маса. BE връща 409, докато не потвърдим изрично.
  allowDuplicateName?: boolean;
}

export const cancelAllBillItems = async (
  billId: number,
  data: CancelAllItemsIn,
): Promise<CloseBillResult> => {
  const response = await api.post<CloseBillResult>(
    `/bills/${billId}/cancel-all-items`,
    data,
  );
  return response.data;
};

export const restoreBill = async (
  billId: number,
  data: RestoreBillIn,
): Promise<CloseBillResult> => {
  const response = await api.post<CloseBillResult>(
    `/bills/${billId}/restore`,
    data,
  );
  return response.data;
};

export const renameBill = async (
  billId: number,
  name: string,
): Promise<BillOut> => {
  const response = await api.patch<BillOut>(`/bills/${billId}/name`, { name });
  return response.data;
};

// Прехвърля цялата сметка на друг сервитьор — променя само bills.waiter_id.
// bill_element.waiter_id (кой е приел конкретната поръчка) остава непроменен,
// за да се вижда в историята кой реално е приел всяка поръчка.
// `allowDuplicateName` се праща само след като операторът е потвърдил в модала:
// без него BE връща 409, ако получателят вече има отворена маса със същото име
// (законно при лична номерация — двама могат да имат маса „1").
export const transferBillWaiter = async (
  billId: number,
  waiterId: number,
  allowDuplicateName = false,
): Promise<BillOut> => {
  const response = await api.patch<BillOut>(`/bills/${billId}/waiter`, {
    waiterId,
    allowDuplicateName,
  });
  return response.data;
};

// Брой отворени маси по сервитьор — „N маси" до всяко име в модала за
// прехвърляне. Сервитьор без отворени маси не присъства в отговора.
export interface WaiterOpenBillsCount {
  waiterId: number;
  count: number;
}

export const getWaiterOpenCounts = async (): Promise<WaiterOpenBillsCount[]> => {
  const response = await api.get<WaiterOpenBillsCount[]>(
    "/bills/waiter-open-counts",
  );
  return response.data;
};

// „Прехвърли всичките ми маси": всички отворени сметки на ТЕКУЩИЯ потребител
// към друг сервитьор. Същият 409-договор като transferBillWaiter — detail
// изброява дублираните имена, FE пита и повтаря с allowDuplicateName.
export const transferAllBillsWaiter = async (
  waiterId: number,
  allowDuplicateName = false,
): Promise<{ transferred: number }> => {
  const response = await api.patch<{ transferred: number }>(
    "/bills/waiter/transfer-all",
    { waiterId, allowDuplicateName },
  );
  return response.data;
};

// Повторно закриване на закрита сметка без касова бележка (total = 0).
// Тялото е същото като на закриването — модалът е същият, включително
// cash-only режима (total: 0) и нефискалните методи. Без paymentMethodId се
// ползва методът по сметката: така „Фискални проблеми" повтаря СЪЩИЯ документ
// (защита срещу дублиран бон).
export interface RetryCloseBillIn {
  paymentMethodId?: number;
  mixedCashAmount?: number;
  mixedCardAmount?: number;
  total?: number;
}

export const closeBillRetry = async (
  billId: number,
  data: RetryCloseBillIn,
): Promise<CloseBillResult> => {
  const response = await api.post<CloseBillResult>(
    `/bills/${billId}/close/retry`,
    data,
  );
  return response.data;
};

// Затворени сметки с неуспешна фискализация + причината от FiscalAPI.
// failed = принтерът отказа (бон НЕ е излизал); timeout = изходът е
// неизвестен; pending = чака се резултат / reconciler.
export interface FiscalIssue {
  bill_id: number;
  bill_name: string;
  closed_at: string | null;
  total_amount: string;
  waiter_id: number | null;
  waiter_name: string | null;
  payment_method_id: number | null;
  payment_method_name: string | null;
  fiscal_status: "pending" | "timeout" | "failed";
  fiscal_error: string | null;
  attempts: number;
  last_attempt_at: string | null;
  request_id: string;
  // Неуредено картово теглене по сметката (пари взети, бон няма) — показва
  // се в списъка, за да не посегне никой към повторно теглене.
  card?: BillCardPayment | null;
}

// Сторно ред: сметката ИМА касова бележка (receipt_number), но кредитното
// известие към нея не е потвърдено. Отделен списък с ОТДЕЛЕН retry път —
// `/storno/retry`, не `/close/retry` (слети, retry би издал втори бон).
export interface StornoIssue extends FiscalIssue {
  receipt_number: string | null;
}

export interface FiscalIssuesResponse {
  issues: FiscalIssue[];
  storno_issues: StornoIssue[];
}

export const getFiscalIssues = async (
  days = 7,
): Promise<FiscalIssuesResponse> => {
  const response = await api.get<FiscalIssuesResponse>(
    `/bills/fiscal-issues`,
    { params: { days } },
  );
  return response.data;
};

// Повтаря сторно с неуспешен/неизвестен изход. Без тяло: BE преизползва
// записания payload на първия опит (при неизвестен изход — и същия
// request_id, така FiscalAPI дедупва и второ кредитно известие не излиза).
export const retryStorno = async (
  billId: number,
): Promise<{ bill_id: number; fiscal_result?: FiscalResult }> => {
  const response = await api.post<{
    bill_id: number;
    fiscal_result?: FiscalResult;
  }>(`/bills/${billId}/storno/retry`);
  return response.data;
};

/** Отстъпки в проценти върху избрани бройки от един или много продуктови реда
 *  наведнъж (добавките следват реда). Част от реда → BE го разделя на два.
 *  Процент 0 маха отстъпката. */
export const applyItemDiscounts = async (
  billId: number,
  data: ApplyItemDiscountsIn,
): Promise<BillOut> => {
  const response = await api.post<BillOut>(
    `/bills/${billId}/apply-item-discounts`,
    data,
  );
  return response.data;
};

/** Нови единични цени на един или много реда наведнъж (BE ги пази като проценти). */
export const setItemPrices = async (
  billId: number,
  data: SetItemPricesIn,
): Promise<BillOut> => {
  const response = await api.post<BillOut>(
    `/bills/${billId}/set-item-prices`,
    data,
  );
  return response.data;
};

export interface PrintPreliminaryBillResult {
  status: "success" | string;
  result?: Record<string, unknown> | null;
}

/**
 * Предварителна сметка — печат на POS принтера на оператора за ОТВОРЕНА сметка.
 *
 * Нищо не се издава и нищо не се фискализира: BE-то праща моментна снимка
 * на сметката към FiscalAPI по свой топик (`print/preliminary_bill`), а
 * документът изрично пише „НЕ Е ФИСКАЛЕН БОН“. Затова е свободно повторим —
 * при грешка операторът просто натиска пак.
 */
export const printPreliminaryBill = async (
  billId: number,
): Promise<PrintPreliminaryBillResult> => {
  const response = await api.post<PrintPreliminaryBillResult>(
    `/bills/${billId}/preliminary-bill/print`,
  );
  return response.data;
};

export interface PrintReceiptCopyResult {
  status: "success" | string;
  result?: Record<string, unknown> | null;
}

/**
 * Копие на фискалния бон на ЗАТВОРЕНА сметка — печата се от електронния
 * журнал (КЛЕН) на апарата, издал бона, адресирано по `receipt_number`.
 *
 * Документът е нефискален (принтерът го маркира като копие) и не променя
 * нищо по сметката, затова е свободно повторим. BE-то отказва с 409 всичко
 * извън четирите условия (затворена, с номер, с ненулева сума, от днес) и с
 * 400, когато апаратът е от марка без такава команда.
 */
export const printReceiptCopy = async (
  billId: number,
): Promise<PrintReceiptCopyResult> => {
  const response = await api.post<PrintReceiptCopyResult>(
    `/bills/${billId}/receipt-copy/print`,
  );
  return response.data;
};
